import { Timestamp } from "firebase/firestore";

import type {
  Dish,
  DishIngredient,
  FirestoreDishDocument,
} from "@dashboard/types/dish";

import { readEntitySource, writeEntitySource } from "./entitySource";

// Converts a firestore dish document to a dish object
export const readDish = (doc: FirestoreDishDocument): Dish => {
  // drop the fields that are not part of the Dish type
  // genDetails is dropped here and not used in the FE.
  const {
    restaurantRef,
    createdAt,
    modifiedAt,
    genDetails,
    source,
    ingredients,
    productIds,
    optionalProductIds,
    optionalAllergenIds,
    mayContainAllergenIds,
    manuallyAddedAllergenIds,
    manuallyAddedPreferenceIds,
    tagIds,
    nutritionEU,
    ...dishRest
  } = doc;

  const dish: Dish = {
    ...dishRest,
    // Ensure all array fields have default empty arrays if null/undefined
    // These fields might be null in Firestore for older documents or during data migration
    ingredients: ingredients || [],
    productIds: productIds || [],
    optionalProductIds: optionalProductIds || [],
    optionalAllergenIds: optionalAllergenIds || [],
    mayContainAllergenIds: mayContainAllergenIds || [],
    manuallyAddedAllergenIds: manuallyAddedAllergenIds || [],
    manuallyAddedPreferenceIds: manuallyAddedPreferenceIds || [],
    tagIds: tagIds || [],
    nutritionEU: nutritionEU ?? null,
    source: readEntitySource(source),
    createdAtMillis: createdAt.toMillis(),
    // If the modified at is not known, we fall back to createdAt.
    // When backfilling modifiedAt was backfilled to null instead of current time or createdAt
    // because this allows for creating reminders.
    // We can change this logic later if we want to show a different date.
    modifiedAtMillis: (modifiedAt || createdAt).toMillis(),
  };

  return dish;
};

// Converts a dish object to a firestore dish document
export const writeDish = (
  dish: Dish,
  isNew: boolean
): FirestoreDishDocument => {
  const updateTs = Timestamp.now();
  const additionalFieldsNewDish = isNew
    ? {
        createdAt: updateTs,
        ingredients: dish.ingredients.map(
          (ingredient) =>
            ({
              ...ingredient,
              optional: false,
            } as DishIngredient)
        ),
      }
    : {};

  // we don't write genDetails or timestamps to the dish object in firestore.
  const { createdAtMillis, modifiedAtMillis, source, ...dishRest } = dish;
  const firestoreDish = {
    ...dishRest,
    ...additionalFieldsNewDish,
    modifiedAt: updateTs,
    source: writeEntitySource(source),
  } as FirestoreDishDocument;

  return firestoreDish;
};
